import { useReactiveVar } from '@apollo/client'
import {
  Grid,
  IconButton,
  IconColor,
  IconName,
  IconSize,
  Select,
  SelectVariant
} from '@focaldata/cin-ui-components'
import React, { useState } from 'react'
import { questionBeingEditedId } from '../../../apollo/apolloClient'
import { isMultilangPocEnabled } from '../../../constants/featureFlags'
import { DraftQuestionnaireEntry } from '../../../data/model/questionnaire'
import { getLanguageLabel } from '../../../utils/HelperFunctions'
import useStyles from './PreviewPanel.styles'
import PreviewQuestions from './PreviewQuestions'

interface Props {
  entries: DraftQuestionnaireEntry[] | undefined
  onClose: () => void
  onRefresh: () => void
  previewLanguage?: string
  onPreviewLanguageChange?: (language: string) => void
  // The languages the dropdown should offer. Expected to be English
  // (master) + any language with at least one stored translation (mirrors
  // the Translations sidebar's "LANGUAGES" list).
  availableLanguages?: ReadonlyArray<string>
}
const PreviewPanelControl: React.FC<Props> = (props: Props) => {
  const {
    entries: flatEntries,
    onClose,
    onRefresh,
    previewLanguage,
    onPreviewLanguageChange,
    availableLanguages = []
  } = props
  const languageOptions = availableLanguages.map((code) => ({
    id: code,
    name: getLanguageLabel(code),
    value: code
  }))
  const { classes } = useStyles()
  const entryBeingEditedId = useReactiveVar(questionBeingEditedId)
  // Refresh re-fetches the questionnaire and restarts the walkthrough; bumping
  // this key remounts PreviewQuestions so it re-opens at the first/edited card.
  const [refreshKey, setRefreshKey] = useState<number>(0)

  return (
    <Grid
      container
      direction="column"
      justifyContent="space-between"
      className={classes.root}
    >
      <Grid
        item
        container
        xs="auto"
        justifyContent="space-between"
        alignItems="center"
      >
        <Grid item className={classes.refresh}>
          <IconButton
            iconName={IconName.Refresh}
            iconSize={IconSize.Medium}
            iconColor={IconColor.Emphasis}
            ariaLabel="Refresh preview"
            onClick={() => {
              onRefresh()
              setRefreshKey((key) => key + 1)
            }}
          />
        </Grid>
        {isMultilangPocEnabled() &&
          previewLanguage &&
          onPreviewLanguageChange &&
          languageOptions.length > 1 && (
            <Grid item style={{ minWidth: 180 }}>
              <Select
                variant={SelectVariant.Outlined}
                options={languageOptions}
                optionValue={previewLanguage}
                ariaLabel="Preview language"
                onChange={(e) =>
                  onPreviewLanguageChange(e.target.value as string)
                }
              />
            </Grid>
          )}
        <Grid item className={classes.close}>
          <IconButton
            iconName={IconName.ChevronDoubleRight}
            iconSize={IconSize.Large}
            iconColor={IconColor.Emphasis}
            ariaLabel="Collapse preview sidebar"
            onClick={() => {
              onClose()
            }}
          />
        </Grid>
      </Grid>
      <Grid item xs="auto" className={classes.previewContainer}>
        <PreviewQuestions
          key={refreshKey}
          entries={flatEntries}
          focusEntryId={entryBeingEditedId}
        />
      </Grid>
    </Grid>
  )
}

export default PreviewPanelControl
